// src/pages/PlanPage.js

import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Navbar from "../components/Navbar";
import ListComponent from "../components/ListComponent";
import { api } from "../api";
import "./PlanPage.css";

function skipKeyFor(user) {
  const id = user?.id || user?.email || "anon";
  return `plan_onboarding_skipped_v1:${id}`;
}

function formatRange(planStart, planEnd) {
  const s = String(planStart || "").trim();
  const e = String(planEnd || "").trim();
  if (!s && !e) return "";
  if (s && e) return `${s} → ${e}`;
  return s || e;
}

export default function PlanPage({ user, onLogout }) {
  const nav = useNavigate();
  const { t } = useTranslation();

  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);

  async function loadPlans() {
    try {
      setLoading(true);
      const res = await api("/me/plans", { cacheTtlMs: 60_000 });
      setPlans(Array.isArray(res?.plans) ? res.plans : []);
    } catch (err) {
      console.error("Failed to load plans", err);
      setPlans([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadPlans();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function goCreatePlan() {
    // user explicitly wants plan creation now -> clear "skipped" flag
    try {
      if (user) localStorage.removeItem(skipKeyFor(user));
    } catch {}
    nav("/onboarding");
  }

  const hasPlans = plans.length > 0;

  const title = useMemo(() => {
    if (!hasPlans) return t("plan.noPlansTitle", { defaultValue: "No plans yet" });
    return t("plan.plansTitle", { defaultValue: "Your plans" });
  }, [hasPlans, t]);

  const emptyText = useMemo(() => {
    return t("plan.noPlansText", {
      defaultValue:
        "There are no active or closed plans at the moment.\nWould you like to create a new plan?",
    });
  }, [t]);

  return (
    <>
      <Navbar user={user} onLogout={onLogout} tab="plan" />

      <div className="page-wrapper">
        <div className="page-container plan-container">
          <main className="plan-stack">
            {loading ? (
              <div className="glass plan-glass">
                <p>{t("common.loading")}</p>
              </div>
            ) : !hasPlans ? (
              <div className="glass plan-glass plan-empty">
                <h2>{title}</h2>

                <p className="plan-empty-text" style={{ whiteSpace: "pre-line" }}>
                  {emptyText}
                </p>

                <div className="plan-empty-actions">
                  <button className="btn primary" type="button" onClick={goCreatePlan}>
                    {t("plan.createButton", { defaultValue: "Create plan" })}
                  </button>
                </div>
              </div>
            ) : (
              <ListComponent
                title={title}
                items={plans}
                itemType="plan"
                enableGroupFilter={false}
                density="normal"
                getId={(p) => p.id}
                getTitle={(p) =>
                  p?.goal ?? "Plan"}
                subtitleForItem={(p) => {
                  const place = p?.trainingPlace ? String(p.trainingPlace) : "";
                  const days = Array.isArray(p?.trainingDays) ? p.trainingDays.join(", ") : "";
                  const range = formatRange(p?.planStart, p?.planEnd);
                  const bits = [place, days, range].filter(Boolean);
                  return bits.join(" • ");
                }}
                sortItems={(items) => {
                  // Active first, then newest by createdAt (fallback to planStart)
                  return [...items].sort((a, b) => {
                    const aActive = a?.isActive ? 1 : 0;
                    const bActive = b?.isActive ? 1 : 0;
                    if (aActive !== bActive) return bActive - aActive;

                    const aTime = Date.parse(a?.createdAt || a?.planStart || "") || 0;
                    const bTime = Date.parse(b?.createdAt || b?.planStart || "") || 0;
                    return bTime - aTime;
                  });
                }}
                onItemClick={(plan) => nav(`/plan/${plan.id}`)}
              />
            )}

            {/* Optional: keep create button also when plans exist */}
            {(!loading && hasPlans) ? (
              <div className="plan-actions">
                <button className="btn primary" type="button" onClick={goCreatePlan}>
                  {t("plan.createButton", { defaultValue: "Create plan" })}
                </button>
              </div>
            ) : null}
          </main>
        </div>
      </div>
    </>
  );
}